import { Injectable } from '@angular/core';
import { StateService } from '@uirouter/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import {
    MonUIRouterTransitionService,
} from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { NOTIFICATION_CONSTANT } from '@client/app/modules/notifications/constant/notification.constant';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { Domain } from '@monsido/modules/models/api/domain';


@Injectable({
    providedIn: 'root',
})
export class NotificationRedirectService {
    private domains: Domain[] = [];

    constructor (
        private stateService: StateService,
        private monEventService: MonEventService,
        private totalDomainsService: TotalDomainsService,
        private sessionService: SessionService,
        private pageService: PageService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
    ) {
        if (this.totalDomainsService.domainsEssential) {
            this.totalDomainsService.domainsEssential.subscribe((updatedDomains: Domain[] | null) => {
                if (updatedDomains) {
                    this.domains = updatedDomains;
                }
            });
        }
    }

    private getScopeId (scopes: any[], type: string): number | null {
        const domainData = scopes.find(scope => scope.type === type);
        return domainData?.id ?? null;
    }

    private getAvailableDomainId (): number | null {
        const currentDomainId = this.sessionService.getDomainId();
        if (currentDomainId) {
            return currentDomainId;
        }

        return Array.isArray(this.domains) ? this.domains.find(domain => domain.active)?.id ?? null : null;
    }

    private async openPage (id: number): Promise<void> {
        try {
            const page = await this.pageService.get(id);
            const params = {
                body: 'pageDetails',
                size: 'lg',
                classes: 'page-details-app',
                data: { page },
            };

            this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
        } catch {
            // Silent catch
        }
    }

    redirect (args: { scopes: any[]; action: string; target: string; target_id?: number }): void {
        let domainId = this.getScopeId(args.scopes, 'Domain');
        const { action, target, target_id } = args;
        const params = this.stateService.params;

        switch (target) {
            case NOTIFICATION_CONSTANT.TARGET.PAGE:
                if (domainId !== null) {
                    if (params.domainId && parseInt(params.domainId) === domainId) {
                        if (target_id !== undefined) {
                            this.openPage(target_id);
                        }
                    } else {
                        this.monUIRouterTransitionService.onTransitionFinished(() =>
                            this.stateService.go('base.customer.domain.dashboard', {
                                domainId,
                                page_id: target_id,
                            }).then(() => {}),
                        );
                    }
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.LINK:
                if (domainId !== null) {
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go('base.customer.domain.qa.broken.links', { domainId }).then(() => {}),
                    );
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.SPELLING_DICTIONARY:
            case NOTIFICATION_CONSTANT.TARGET.SPELLING_CONFIRMED:
            case NOTIFICATION_CONSTANT.TARGET.SPELLING_IGNORED:
                domainId = this.getAvailableDomainId();
                if (domainId) {
                    const state = target === NOTIFICATION_CONSTANT.TARGET.SPELLING_DICTIONARY
                        ? 'base.customer.domain.qa.spellcheck.dictionary'
                        : 'base.customer.domain.qa.spellcheck.pages';
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go(state, { domainId }).then(() => {}),
                    );
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_ERRORS_PAGE:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_SOURCE_CODE:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_CHECK:
            case NOTIFICATION_CONSTANT.TARGET.ACCESSIBILITY_ERROR:
                if (domainId !== null) {
                    const isReviewedOrIgnored = [
                        NOTIFICATION_CONSTANT.ACTION.ACCESSIBILITY_CHECK_REVIEWED,
                        NOTIFICATION_CONSTANT.ACTION.ACCESSIBILITY_CHECK_IGNORED,
                    ].includes(action);

                    const state = isReviewedOrIgnored
                        ? 'base.customer.domain.accessibility.ignored_pages'
                        : 'base.customer.domain.accessibility.pages';

                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go(state, { domainId }).then(() => {}),
                    );
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.SEO_ISSUE:
            case NOTIFICATION_CONSTANT.TARGET.SEO_PAGE_ISSUE:
                if (domainId !== null) {
                    const state = target === NOTIFICATION_CONSTANT.TARGET.SEO_ISSUE
                        ? 'base.customer.domain.seo.issues'
                        : 'base.customer.domain.seo.pages';
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go(state, { domainId }).then(() => {}),
                    );
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.REQUEST_SCAN:
                if (domainId !== null && target_id !== null) {
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go('base.customer.domain.dashboard', { domainId, page_id: target_id }).then(() => {}),
                    );
                }
                break;

            case NOTIFICATION_CONSTANT.TARGET.EXPORT:
                this.monEventService.run(MON_EVENTS.LOAD_DIALOG, {
                    params: { body: 'csvExportsOverlay' },
                });
                break;

            case NOTIFICATION_CONSTANT.TARGET.DOMAIN:
                if (action === NOTIFICATION_CONSTANT.ACTION.COOKIES_FOUND) {
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go('base.customer.domain.data_privacy.consent_overview.cookie_overview', {
                            domainId: target_id,
                        }).then(() => {}),
                    );
                } else {
                    this.monUIRouterTransitionService.onTransitionFinished(() =>
                        this.stateService.go('base.customer.domain.dashboard', { domainId: target_id }).then(() => {}),
                    );
                }
                break;
        }
    }
}
